const BASE_URL = 'https://toxsee.onrender.com';
const STORAGE_KEY = 'toxsee_medications';

function normalizeUsername(username) {
  return String(username || '').trim().toLowerCase();
}

function getLocal() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function setLocal(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function getLocalForUser(username) {
  const normalized = normalizeUsername(username);
  return getLocal().filter(
    m => normalizeUsername(m.username) === normalized
  );
}

export async function fetchMedications(username) {
  const normalized = normalizeUsername(username);
  try {
    const res = await fetch(
      `${BASE_URL}/api/medications/${encodeURIComponent(normalized)}`
    );
    if (res.ok) {
      const { medications } = await res.json();
      const others = getLocal().filter(
        m => normalizeUsername(m.username) !== normalized
      );
      setLocal([...(medications || []), ...others]);
      return medications || [];
    }
  } catch {
    // fall through to local cache
  }
  return getLocalForUser(normalized);
}

export async function addMedication(username, medication) {
  const normalized = normalizeUsername(username);
  const localMedication = {
    _id: `local-med-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    username: normalized,
    medicationName: medication.medicationName,
    productName: medication.productName || medication.medicationName || '',
    din: medication.din || '',
    activeIngredientGroupNumber: medication.activeIngredientGroupNumber || '',
    dosage: medication.dosage,
    frequency: medication.frequency,
    instructions: medication.instructions || '',
    startDate: medication.startDate,
    endDate: medication.endDate || null,
    status: medication.status || 'current',
    reminderEnabled: Boolean(medication.reminderEnabled),
    reminderTimes: Array.isArray(medication.reminderTimes) ? medication.reminderTimes : [],
    logs: [],
    createdAt: new Date().toISOString(),
  };

  try {
    const res = await fetch(`${BASE_URL}/api/medications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: normalized, ...medication }),
    });

    if (res.ok) {
      const { medication: saved } = await res.json();
      setLocal([saved, ...getLocal()]);
      return saved;
    }
  } catch {
    // fallback local
  }

  setLocal([localMedication, ...getLocal()]);
  return localMedication;
}

export async function updateMedication(medicationId, updates) {
  const idStr = String(medicationId);
  const local = getLocal();
  const index = local.findIndex(m => String(m._id) === idStr);
  if (index < 0) return null;

  const merged = {
    ...local[index],
    ...updates,
    endDate: Object.prototype.hasOwnProperty.call(updates, 'endDate')
      ? (updates.endDate || null)
      : local[index].endDate,
  };
  local[index] = merged;
  setLocal(local);

  if (!idStr.startsWith('local-med-') && !idStr.startsWith('mock-med-')) {
    try {
      const res = await fetch(`${BASE_URL}/api/medications/${idStr}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const { medication } = await res.json();
        const refreshed = getLocal();
        const refreshedIdx = refreshed.findIndex(m => String(m._id) === idStr);
        if (refreshedIdx >= 0) {
          refreshed[refreshedIdx] = medication;
          setLocal(refreshed);
        }
        return medication;
      }
    } catch {
      // keep local optimistic update
    }
  }

  return merged;
}

export async function logMedicationAction(medicationId, status, timestamp = null) {
  const idStr = String(medicationId);
  const local = getLocal();
  const index = local.findIndex(m => String(m._id) === idStr);
  if (index < 0) return null;

  const entry = {
    logId: `log-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    status,
    timestamp: timestamp || new Date().toISOString(),
  };

  const logs = Array.isArray(local[index].logs) ? local[index].logs : [];
  local[index] = {
    ...local[index],
    logs: [entry, ...logs].slice(0, 365),
  };
  setLocal(local);

  if (!idStr.startsWith('local-med-') && !idStr.startsWith('mock-med-')) {
    try {
      const res = await fetch(`${BASE_URL}/api/medications/${idStr}/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logId: entry.logId, status, timestamp: entry.timestamp }),
      });
      if (res.ok) {
        const { medication } = await res.json();
        const refreshed = getLocal();
        const refreshedIdx = refreshed.findIndex(m => String(m._id) === idStr);
        if (refreshedIdx >= 0) {
          refreshed[refreshedIdx] = medication;
          setLocal(refreshed);
        }
        return medication;
      }
    } catch {
      // keep local optimistic update
    }
  }

  return local[index];
}

export async function deleteMedicationLogEntry(medicationId, entry) {
  const idStr = String(medicationId);
  const local = getLocal();
  const index = local.findIndex(m => String(m._id) === idStr);
  if (index < 0) return null;

  const targetLogId = String(entry?.logId || '');
  const targetTimestamp = String(entry?.timestamp || '');
  const targetStatus = String(entry?.status || '');

  const logs = Array.isArray(local[index].logs) ? local[index].logs : [];
  let removed = false;
  const nextLogs = logs.filter((log) => {
    const sameByLogId = targetLogId && String(log.logId || log._id || '') === targetLogId;
    const sameByFallback = !targetLogId
      && String(log.timestamp || '') === targetTimestamp
      && String(log.status || '') === targetStatus;
    const shouldRemove = !removed && (sameByLogId || sameByFallback);
    if (shouldRemove) removed = true;
    return !shouldRemove;
  });

  local[index] = {
    ...local[index],
    logs: nextLogs,
  };
  setLocal(local);

  if (!idStr.startsWith('local-med-') && !idStr.startsWith('mock-med-')) {
    try {
      const params = new URLSearchParams();
      if (targetTimestamp) params.set('timestamp', targetTimestamp);
      if (targetStatus) params.set('status', targetStatus);
      const logPath = targetLogId ? encodeURIComponent(targetLogId) : 'by-timestamp';
      const res = await fetch(`${BASE_URL}/api/medications/${idStr}/log/${logPath}?${params.toString()}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        const { medication } = await res.json();
        const refreshed = getLocal();
        const refreshedIdx = refreshed.findIndex(m => String(m._id) === idStr);
        if (refreshedIdx >= 0) {
          refreshed[refreshedIdx] = medication;
          setLocal(refreshed);
        }
        return medication;
      }
    } catch {
      // keep local optimistic delete
    }
  }

  return local[index];
}

const MOCK_MEDICATIONS = [
  { medicationName: 'Letrozole', dosage: '2.5 mg', frequency: 'Once daily', instructions: 'Take at the same time each day', status: 'current' },
  { medicationName: 'Ondansetron', dosage: '8 mg', frequency: 'Every 8 hours as needed', instructions: 'Use for nausea after treatment', status: 'current' },
  { medicationName: 'Dexamethasone', dosage: '4 mg', frequency: 'Twice daily', instructions: 'Take with food', status: 'past' },
  { medicationName: 'Acetaminophen', dosage: '500 mg', frequency: 'Every 6 hours as needed', instructions: 'Do not exceed 4 g/day', status: 'current' },
];

export function seedMedicationMockData(username) {
  const normalized = normalizeUsername(username);
  if (getLocalForUser(normalized).length > 0) return;

  const now = new Date();
  const seeded = MOCK_MEDICATIONS.map((med, i) => {
    const startDate = new Date(now);
    startDate.setDate(startDate.getDate() - (i + 3) * 6);

    const logs = Array.from({ length: 5 }, (_, j) => {
      const ts = new Date(now);
      ts.setDate(ts.getDate() - j);
      ts.setHours(8 + (j % 3) * 4);
      return {
        logId: `mock-log-${i}-${j}-${Date.now()}`,
        status: ['taken', 'taken', 'skipped', 'taken', 'missed'][j % 5],
        timestamp: ts.toISOString(),
      };
    });

    return {
      _id: `mock-med-${i}-${Date.now()}`,
      username: normalized,
      medicationName: med.medicationName,
      productName: med.medicationName,
      din: '',
      activeIngredientGroupNumber: '',
      dosage: med.dosage,
      frequency: med.frequency,
      instructions: med.instructions,
      startDate: startDate.toISOString(),
      endDate: med.status === 'past' ? new Date(startDate.getTime() + 28 * 86400000).toISOString() : null,
      status: med.status,
      reminderEnabled: med.status === 'current',
      reminderTimes: med.status === 'current' ? ['08:00'] : [],
      logs,
      createdAt: startDate.toISOString(),
    };
  });

  const others = getLocal().filter(m => normalizeUsername(m.username) !== normalized);
  setLocal([...seeded, ...others]);
}
